import { ForwardedRef, forwardRef, ReactNode, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Position, Range } from 'types'
import { DraggableContext, DraggableContextProps } from 'ui'
import Loop from '../loop'
import { ArtboardLoopRef, ArtboardRef } from '../types/refs'
import { ArtboardContext } from './context'
import useLayers from './hooks/use-layers'
import { Board } from './style'
import { getMergedFrame, getMergedFrames, getMergedSprite } from './utils'

type Props = {
    children?: ReactNode
    className?: string
    crop?: Position
    disabled?: boolean
    draggableOptions?: Partial<DraggableContextProps>
    duration?: number
    fps?: number
    height: number
    paused?: boolean
    scale?: number
    timing?: Range
    width: number
    onDurationChange?: (duration: number) => void
}

const Artboard = (
    {
        children,
        className,
        crop,
        disabled = false,
        draggableOptions = {},
        duration: durationProp,
        fps = 16,
        height,
        paused = false,
        scale = 1,
        timing,
        width,
        onDurationChange,
    }: Props,
    ref: ForwardedRef<ArtboardRef>
) => {
    const loopRef = useRef<ArtboardLoopRef>(null)
    const { nodes, refs: layers } = useLayers(children)
    const [duration, setDuration] = useState<number>(durationProp || 0)

    useImperativeHandle(ref, () => ({
        getFrame: (frame: number, excludeLayerIndexes: number[] = []) => {
            return getMergedFrame(layers, frame, { crop, duration, fps, height, excludeLayerIndexes, width })
        },
        getFrames: (excludeLayerIndexes: number[] = [], count?: number) => {
            const durationInFrames = Math.round(duration * fps)

            return getMergedFrames(layers, count || durationInFrames, {
                crop,
                duration,
                fps,
                height,
                excludeLayerIndexes,
                width,
            })
        },
        getSprite: (excludeLayerIndexes: number[] = [], count?: number) => {
            const durationInFrames = Math.round(duration * fps)

            return getMergedSprite(layers, count || durationInFrames, {
                crop,
                duration,
                fps,
                height,
                excludeLayerIndexes,
                width,
            })
        },
        getProgress: () => {
            return loopRef.current?.getProgress() || 0
        },
        getTime: () => {
            return loopRef.current?.getTime() || 0
        },
        goToFrame: (frame: number) => {
            loopRef.current?.goToFrame(frame)
        },
        goToProgress: (progress: number) => {
            loopRef.current?.goToProgress(progress)
        },
        goToTime: (seconds: number) => {
            loopRef.current?.goToTime(seconds)
        },
    }))

    useEffect(() => {
        if (typeof durationProp === 'number') {
            setDuration(durationProp)
        }
    }, [durationProp])

    useEffect(() => {
        if (duration !== durationProp) {
            onDurationChange?.(duration)
        }
    }, [duration])

    return (
        <ArtboardContext.Provider
            value={{
                autoDuration: typeof durationProp !== 'number',
                crop,
                duration,
                fps,
                height,
                layers,
                paused,
                scale,
                width,
                setDuration,
            }}
        >
            <DraggableContext {...draggableOptions}>
                <Loop duration={duration} fps={fps} paused={paused || !duration} ref={loopRef} timing={timing}>
                    <Board
                        className={className}
                        style={{ height, pointerEvents: disabled ? 'none' : undefined, width }}
                    >
                        {nodes}
                    </Board>
                </Loop>
            </DraggableContext>
        </ArtboardContext.Provider>
    )
}

export default forwardRef(Artboard)
